import { useEmits } from '../hooks/use-emits';

export type DefineEmitsType<T extends object> = {
  [K in keyof T]: T[K] extends Array<unknown>
    ? T[K] extends [] ? (...args: any[]) => void : (...args: T[K]) => void
    : (value: T[K]) => void;
};

type UnionToIntersection<U> =
  (U extends U ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

  type UnionToTuple<T> = UnionToIntersection<
  T extends T ? () => T : never
> extends () => infer R
  ? [...UnionToTuple<Exclude<T, R>>, R]
  : [];

// 对联合类型按键名排序
type SortedUnionToTuple<T extends string> = UnionToTuple<
  T extends string ? T : never
>;

/**
 * 用于增强emit类型提示的工具函数。返回的对象包含 `defineEmitType` 和 `useEmits` 方法。
 *
 * Example runtime usage:
 * ```ts
 * // 事件类型的定义参考vue3的defineEmits宏的支持写法，https://vuejs.org/api/sfc-script-setup.html#defineprops-defineemits
 * interface Event {
 *   change: [value: string]; // 具名元组语法
 *   close: [];
 * }
 *
 * const { defineEmitType, useEmits } = createEmits<Event>();
 *
 * const emits = defineEmitType(['change', 'update']);
 *
 * defineComponent({
 *   emits,
 *   setup(props, { emit: contextEmit }) {
 *     const emit = useEmits();
 *     // 此时emit也会有相应的类型提示
 *     emit('change', 'new value');
 *     // 此时类型会报错
 *     emit('change', 111);
 *     // 此处也会报错，并且会有相应的类型提示
 *     contextEmit('change', 1111);
 *   }
 * })
 *```
 */
export function createEmits<T extends object>() {
  return {
    /**
     * 做emits的强制类型增强，返回emits的数组
     * @param emits
     * @returns
     */
    defineEmitType: (emits: SortedUnionToTuple<keyof T & string>) => emits as unknown as DefineEmitsType<T>,
    /**
     * 用于声明与发送组件的事件hooks，仅支持在setup下使用。预期参数与组件的 emit 选项相同。
     *
     * Example runtime usage:
     *```ts
     * const emit = useEmits<EmitEvents>()
     * emit('change')
     * emit('update', 1)
     *```
     */
    useEmits: () => useEmits<T>(),
  };
}
